
  <!-- https://www.npmjs.com/package/vue-carousel -->
<template>
  <div v-observe-visibility="visibilityChanged">
    <carousel v-if="ready && items"
              v-show="isCollapsed"
              :perPage="1"
              :adjustableHeight="false"
              :loop="true"
              :autoplayTimeout="6000"
              :autoplay="isCollapsed && isDisplayed"
              :paginationEnabled="true"
              :paginationPosition="`bottom-overlay`"
              @pageChange="onSlideChange">
      <slide v-for="item in items"
             :key="itemId(item)"
             class="position-relative"
             @slideclick="onSlideClick">
        <img :src="itemImage(item)" class="w-100 p-0 rounded-0">
        <div v-show="isItemLocked(item)" class="asset-badge fas fa-lock"></div>
      </slide>
    </carousel>

    <template v-for="item in items">
      <Asset v-if="item.isVod"
             v-show="shouldShowItem(item)"
             :key="itemId(item)"
             :ref="`slide_${itemId(item)}`"
             :item="item"
             :promo-mode="true"/>
      <Poll v-if="item.isPoll"
            v-show="shouldShowItem(item)"
            :key="itemId(item)"
            :ref="`slide_${itemId(item)}`"
            :item="item"
            :isOpenForAnonymous="true"
            :promo-mode="true"/>
      <Category v-if="item.isCategory || item.isSwimlane"
                v-show="shouldShowItem(item)"
                :key="itemId(item)"
                :ref="`slide_${itemId(item)}`"
                :item="item"
                :promo-mode="true"/>
      <Banner v-if="item.isBanner"
              v-show="shouldShowItem(item)"
              :key="itemId(item)"
              :ref="`slide_${itemId(item)}`"
              :item="item"
              :promo-mode="true"/>
      <Article v-if="item.isArticle"
              v-show="shouldShowItem(item)"
              :key="itemId(item)"
              :ref="`slide_${itemId(item)}`"
              :item="item"
              :promo-mode="true"/>
    </template>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { spinnakerApi } from '../js/SpinnakerApi'
import { ObserveVisibility } from 'vue-observe-visibility'
import { Carousel, Slide } from 'vue-carousel'
import Article from '../views/Article'
import Asset from '../views/Asset.vue'
import Banner from './Banner.vue'
import Category from './Category.vue'
import Poll from '../views/SimplePoll.vue'

/*
 * UGLY AS HELL !!!! (but works :/)
 *
 * The issue:
 *   Carousel skips over more than one item and/or keeps rotating even if carousel is expanded.
 * How to reproduce:
 *   Expand carousel and swipe it to another slide.
 * Root cause:
 *   Carousel component is not clearing interval before creating new one
 * Solution:
 *   Indirectly force execution of clearInterval (via pauseAutoplay method) before every setInterval call
 *   that is used in startAutoplay method.
 */
const originStartAutoplay = Carousel?.mixins?.[0]?.methods?.startAutoplay
if (typeof originStartAutoplay === 'function') {
  Carousel.mixins[0].methods.startAutoplay = function () {
    this.pauseAutoplay()
    originStartAutoplay.call(this)
  }
}

export default {
  props: ['category', 'isDisplayed'],
  directives: { 'observe-visibility': ObserveVisibility },
  components: {
    Article,
    Carousel,
    Slide,
    Asset,
    Poll,
    Category,
    Banner
  },
  data: function () {
    return {
      items: null,
      ready: false,
      currentSlide: 0,
      isCollapsed: true
    }
  },
  computed: {
    ...mapState({
      isBeta: state => state.isBeta,
      isRegisteredUser: state => state.isRegisteredUser
    }),
    currentSlideItem () {
      return this.items[this.currentSlide]
    }
  },
  created: function () {
    this.storeUnsubscribe = this.$store.subscribe(mutation => {
      if (mutation.type === 'navigation/unlock') {
        this.collapseCarousel()
      }
    })
    this.loadItems()
  },
  beforeDestroy () {
    this.storeUnsubscribe()
  },
  watch: {
    isDisplayed (newIsDisplayed) {
      const isNoLongerVisible = !newIsDisplayed
      if (isNoLongerVisible) {
        this.collapseCarousel()
      }
    }
  },
  methods: {
    loadItems: async function () {
      const promoItems = await spinnakerApi.getCategoryItems(this.category.id)
      if (promoItems && promoItems.items && Array.isArray(promoItems.items)) {
        this.items = promoItems.items
        this.items.forEach((item) => { item.categoryId = this.category.id })
        console.info(`Carousel: ${this.items.length} items fetched`)
      } else {
        console.info(`Carousel: no content fetched`)
      }
    },
    itemImage (item) {
      return item.promoHeroImage || item.promo
    },
    itemId (item) {
      return item.id || item.progId
    },
    isItemLocked: function (item) {
      if (item.isBanner) {
        return false
      }
      if (item.isPlaceholder) {
        return false
      }
      if (this.isBeta && !this.isRegisteredUser) {
        return true
      }
      const signUpRequired = item.flags?.flags?.includes('SignupRequired')
      return signUpRequired && !this.isRegisteredUser
    },
    shouldShowItem (item) {
      return !this.isCollapsed && this.currentSlideItem === item
    },
    expandCarousel () {
      if (this.isCollapsed && !this.isAlwaysCollapsed(this.currentSlide)) {
        console.info('Carousel: expanding')
        this.isCollapsed = false
        this.$store.commit('navigation/lock')
      }
    },
    collapseCarousel () {
      if (!this.isCollapsed) {
        this.isCollapsed = true
        console.info('Carousel: collapsing')
        this.$store.commit('navigation/unlock')
      }
    },
    isAlwaysCollapsed (page) {
      const item = this.items[page]
      return item.isCategory || item.isSwimlane || item.isBanner
    },
    visibilityChanged (isVisible, entry) {
      if (isVisible) {
        console.info('showing carousel now because view port is ready')
        this.ready = true
      }
    },
    onSlideChange (slide) {
      this.collapseCarousel()
      this.currentSlide = slide
    },
    onSlideClick () {
      this.expandCarousel()
      const currentSlideRef = `slide_${this.itemId(this.currentSlideItem)}`
      this.$refs[currentSlideRef][0].onItemClick()
    }
  }
}
</script>

<style>
.VueCarousel-dot {
  width: 4px !important;
  height: 4px !important;
  padding: 0 !important;
  margin: 3px !important;
  border: 1px solid white !important;
  background-color: white !important;
}
.VueCarousel-dot--active {
  background-color: transparent !important;
}
</style>
